import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

interface PasswordStrengthBarProps {
  password: string;
}

function getStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: '', color: '' };

  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);
  const complexityMet = [hasUpper, hasLower, hasDigit].filter(Boolean).length;

  if (password.length < 8) {
    return { score: 1, label: 'Weak', color: '#f44336' };
  }

  if (complexityMet < 2) {
    return { score: 2, label: 'Fair', color: '#ff9800' };
  }

  if (complexityMet >= 2 && (complexityMet < 3 || password.length < 12)) {
    return { score: 3, label: 'Good', color: '#fdd835' };
  }

  return { score: 4, label: 'Strong', color: '#4caf50' };
}

const PasswordStrengthBar: React.FC<PasswordStrengthBarProps> = ({ password }) => {
  const { score, label, color } = getStrength(password);

  if (score === 0) return null;

  return (
    <Box sx={{ mt: 0.5, mb: 1 }}>
      <LinearProgress
        variant="determinate"
        value={(score / 4) * 100}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.1)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 },
        }}
      />
      <Typography variant="caption" sx={{ color, mt: 0.25, display: 'block' }}>
        {label}
      </Typography>
    </Box>
  );
};

export default PasswordStrengthBar;
